/**
* ============================================================================
* SIZE CHART — JavaScript Controller
* ============================================================================
* Drop this into: assets/size-chart.js
* Include in theme: <script src="{{ 'size-chart.js' | asset_url }}" defer></script>
*
* Handles:
* - Modal open/close with focus trapping
* - JSON → table rendering
* - Unit system toggle (imperial/metric)
* - Active variant row highlighting
* - Keyboard accessibility (Escape to close)
* ============================================================================
*/
(function () {
    'use strict';
    const SELECTORS = {
        modal: '[data-size-chart-modal]',
        open: '[data-size-chart-open]',
        close: '[data-size-chart-close]',
        chartData: '[data-size-chart-data]',
        unitBtn: '[data-unit]',
        unitPanel: '[data-unit-panel]',
        table: '.sc-table',
    };
    const CLASSES = {
        closing: 'sc-modal--closing',
        activeBtn: 'sc-unit-toggle__btn--active',
        activeRow: 'sc-table__row--active',
        highlightCell: 'sc-table__highlight',
    };
    let previouslyFocusedEl = null;
    /* ── Initialize ── */
    function init() {
        const modal = document.querySelector(SELECTORS.modal);
        if (!modal) return;
        // Render tables from embedded JSON
        renderAllTables();
        moveModalToBody()
        // Open triggers
        document.querySelectorAll(SELECTORS.open).forEach((btn) => {
            btn.addEventListener('click', () => openModal(modal));
        });
        // Close triggers (backdrop + close buttons)
        modal.querySelectorAll(SELECTORS.close).forEach((el) => {
            el.addEventListener('click', () => closeModal(modal));
        });
        // Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !modal.hidden) {
                closeModal(modal);
            }
        });
        // Unit toggle
        modal.querySelectorAll(SELECTORS.unitBtn).forEach((btn) => {
            btn.addEventListener('click', () => switchUnit(modal, btn.dataset.unit));
        });
        // Listen for Shopify variant changes to highlight matching row
        document.addEventListener('variant:changed', (e) => {
            highlightVariantRow(modal, e.detail);
        });
        // Also listen for the standard Shopify section event
        document.addEventListener('shopify:section:load', () => {
            renderAllTables();
        });
    }
    /* ── Modal Open/Close ── */
    function openModal(modal) {
        previouslyFocusedEl = document.activeElement;
        modal.hidden = false;
        modal.classList.remove(CLASSES.closing);
        document.body.style.overflow = 'hidden';
        // Focus the close button
        const closeBtn = modal.querySelector('.sc-modal__close');
        if (closeBtn) closeBtn.focus();
        // Trap focus inside modal
        modal.addEventListener('keydown', trapFocus);
    }
    function closeModal(modal) {
        modal.classList.add(CLASSES.closing);
        const onEnd = () => {
            modal.hidden = true;
            modal.classList.remove(CLASSES.closing);
            document.body.style.overflow = '';
            modal.removeEventListener('keydown', trapFocus);
            if (previouslyFocusedEl) {
                previouslyFocusedEl.focus();
                previouslyFocusedEl = null;
            }
            modal.removeEventListener('animationend', onEnd);
        };
        modal.addEventListener('animationend', onEnd, { once: false });
        // Fallback if animation doesn't fire
        setTimeout(() => {
            if (!modal.hidden) onEnd();
        }, 400);
    }
    function trapFocus(e) {
        if (e.key !== 'Tab') return;
        const modal = e.currentTarget;
        const focusable = modal.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }
    /* ── Table Rendering ── */
    function renderAllTables() {
        document.querySelectorAll(SELECTORS.chartData).forEach((script) => {
            const unit = script.dataset.sizeChartData; // 'imperial' or 'metric'
            const modal = script.closest('[data-size-chart-modal]');
            if (!modal) return;

            const panel = modal.querySelector(`[data-unit-panel="${unit}"]`);
            if (!panel) return;
            const table = panel.querySelector(SELECTORS.table);
            if (!table) return;
            try {
                const data = JSON.parse(script.textContent);
                renderTable(table, data);
            } catch (err) {
                console.warn('[SizeChart] Failed to parse chart JSON:', err);
            }
        });
    }
    function renderTable(tableEl, data) {
        if (!data || !data.headers || !data.rows) return;
        const highlightCol = data.highlight_column ?? 0;
        // Build thead
        const thead = document.createElement('thead');
        const headerRow = document.createElement('tr');
        data.headers.forEach((header, i) => {
            const th = document.createElement('th');
            th.textContent = header;
            th.scope = 'col';
            if (i === highlightCol) th.classList.add(CLASSES.highlightCell);
            headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        // Build tbody
        const tbody = document.createElement('tbody');
        data.rows.forEach((row) => {
            const tr = document.createElement('tr');
            row.forEach((cell, i) => {
                const td = document.createElement('td');
                td.textContent = cell;
                if (i === highlightCol) td.classList.add(CLASSES.highlightCell);
                tr.appendChild(td);
            });
            tbody.appendChild(tr);
        });
        // Replace table content
        tableEl.innerHTML = '';



        const caption = tableEl.querySelector('caption');
        tableEl.innerHTML = '';
        if (caption) {
            tableEl.appendChild(caption.cloneNode(true));
        }


        tableEl.appendChild(thead);
        tableEl.appendChild(tbody);
    }
    /* ── Unit Toggle ── */
    function switchUnit(modal, unit) {
        // Update buttons
        modal.querySelectorAll(SELECTORS.unitBtn).forEach((btn) => {
            btn.classList.toggle(CLASSES.activeBtn, btn.dataset.unit === unit);
            btn.setAttribute('aria-selected', btn.dataset.unit === unit);
        });
        // Show/hide panels
        modal.querySelectorAll(SELECTORS.unitPanel).forEach((panel) => {
            panel.hidden = panel.dataset.unitPanel !== unit;
        });
    }
    /* ── Variant Row Highlighting ── */
    function highlightVariantRow(modal, variantDetail) {
        if (!variantDetail) return;
        // Try to match by the first option (typically "Size")
        const sizeValue = variantDetail.option1 || variantDetail.title || '';
        if (!sizeValue) return;
        modal.querySelectorAll(`${SELECTORS.table} tbody tr`).forEach((tr) => {
            const firstCell = tr.querySelector('td');
            const match =
                firstCell &&
                firstCell.textContent.trim().toLowerCase() === sizeValue.toLowerCase();
            tr.classList.toggle(CLASSES.activeRow, match);
        });
    }
    /* ── Boot ── */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }


    function moveModalToBody() {
  const modal = document.querySelector('[data-size-chart-modal]');
  if (!modal) return;

  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }
}

})();